if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      static senseActiveModal = null;

      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.senseLoadingState = this.querySelector('[data-sense-complementary-loading]');
        this.senseErrorState = this.querySelector('[data-sense-complementary-error]');
        this.senseViewProduct = this.querySelector('[data-sense-complementary-view-product]');
        this.senseAbortController = null;
        this.senseRequestToken = null;
        this.sensePendingOpener = null;
        this.senseStickyState = null;
        this.senseOwnerObserver = null;
        this.senseSubmitLabelObserver = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        super.connectedCallback();
        if (!this.isSenseModal) return;

        document.querySelectorAll('quick-add-modal[data-sense-complementary-modal]').forEach((modal) => {
          if (modal === this || modal.dataset.senseModalKey !== this.dataset.senseModalKey) return;
          modal.disposeSenseModal?.();
        });

        this.initializeSenseOwnerObserver();
        this.initializeSenseSubmitLabelObserver();
      }

      get isSenseModal() {
        return this.hasAttribute('data-sense-complementary-modal');
      }

      initializeSenseOwnerObserver() {
        const ownerSection = this.dataset.senseOwnerSection
          ? document.getElementById(`shopify-section-${this.dataset.senseOwnerSection}`)
          : null;
        const ownerParent = ownerSection?.parentNode;

        if (!ownerSection || !ownerParent) return;

        this.senseOwnerObserver?.disconnect();
        this.senseOwnerObserver = new MutationObserver(() => {
          if (ownerSection.isConnected) return;
          this.disposeSenseModal();
        });
        this.senseOwnerObserver.observe(ownerParent, { childList: true });
      }

      initializeSenseSubmitLabelObserver() {
        if (this.senseSubmitLabelObserver || !this.modalContent) return;

        this.senseSubmitLabelObserver = new MutationObserver((mutations) => {
          if (!this.hasAttribute('open') || !mutations.some((mutation) => mutation.addedNodes.length)) return;

          const submitButton = this.modalContent.querySelector('.product-form__submit[type="submit"]');
          if (submitButton) this.applySenseSubmitLabel(submitButton, submitButton);
        });
        this.senseSubmitLabelObserver.observe(this.modalContent, { childList: true });
      }

      disposeSenseModal() {
        if (this.hasAttribute('open')) {
          this.hide(true);
        } else {
          this.cleanupSenseModal();
        }

        this.senseOwnerObserver?.disconnect();
        this.senseOwnerObserver = null;
        this.senseSubmitLabelObserver?.disconnect();
        this.senseSubmitLabelObserver = null;
        this.remove();
      }

      hide(preventFocus = false) {
        if (this.isSenseModal) this.cleanupSenseModal();

        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        if (this.isSenseModal) return this.showSenseModal(opener);

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      showSenseModal(opener) {
        if (!opener) return;

        const activeModal = QuickAddModal.senseActiveModal;
        if (activeModal && activeModal !== this) {
          if (activeModal.hasAttribute('open')) {
            activeModal.hide(true);
          } else {
            activeModal.cleanupSenseModal();
          }
        }

        if (this.hasAttribute('open')) {
          this.hide(true);
        } else {
          this.cleanupSenseModal();
        }

        const productUrl = opener.getAttribute('data-product-url');
        if (!productUrl) return;

        const requestToken = Symbol('sense-complementary-request');
        this.senseRequestToken = requestToken;
        this.senseAbortController = new AbortController();
        this.sensePendingOpener = opener;
        QuickAddModal.senseActiveModal = this;

        this.setSenseOpenerLoading(opener, true);
        this.showSenseLoading();
        this.setAttribute('data-sense-loading', '');

        try {
          super.show(opener);
          this.suppressSenseStickyAtc();
        } catch (error) {
          this.cleanupSenseModal();
          if (this.hasAttribute('open')) super.hide();
          throw error;
        }

        return fetch(productUrl, { signal: this.senseAbortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Product request failed with status ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (this.senseRequestToken !== requestToken) return;

            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Product response does not contain product-info');

            this.preprocessHTML(productElement);
            if (this.hasSenseExecutableScripts(productElement)) {
              throw new Error('Product summary contains executable scripts');
            }
            if (this.senseRequestToken !== requestToken) return;

            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.showSenseContent();
          })
          .catch((error) => {
            if (this.senseRequestToken !== requestToken || error.name === 'AbortError') return;
            this.showSenseError();
          })
          .finally(() => {
            if (this.senseRequestToken !== requestToken) return;

            this.setSenseOpenerLoading(opener, false);
            this.senseAbortController = null;
            this.senseRequestToken = null;
            this.sensePendingOpener = null;
            this.removeAttribute('data-sense-loading');
          });
      }

      setSenseOpenerLoading(opener, isLoading) {
        if (!opener) return;

        if (isLoading) {
          opener.setAttribute('aria-disabled', 'true');
          opener.setAttribute('aria-busy', 'true');
        } else {
          opener.removeAttribute('aria-disabled');
          opener.removeAttribute('aria-busy');
        }
        opener.classList.toggle('loading', isLoading);
        opener.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      showSenseLoading() {
        this.modalContent.innerHTML = '';
        this.modalContent.setAttribute('aria-busy', 'true');
        this.senseLoadingState?.removeAttribute('hidden');
        this.senseErrorState?.setAttribute('hidden', '');
        this.removeAttribute('data-sense-error');
      }

      showSenseContent() {
        this.modalContent.removeAttribute('aria-busy');
        this.senseLoadingState?.setAttribute('hidden', '');
        this.senseErrorState?.setAttribute('hidden', '');
        this.removeAttribute('data-sense-error');
      }

      showSenseError() {
        this.modalContent.innerHTML = '';
        this.modalContent.removeAttribute('aria-busy');
        this.senseLoadingState?.setAttribute('hidden', '');
        this.senseErrorState?.removeAttribute('hidden');
        this.setAttribute('data-sense-error', '');
      }

      cleanupSenseModal() {
        this.senseAbortController?.abort();
        this.senseAbortController = null;
        this.senseRequestToken = null;
        this.removeAttribute('data-sense-loading');
        this.removeAttribute('data-sense-error');

        if (this.sensePendingOpener) this.setSenseOpenerLoading(this.sensePendingOpener, false);
        this.sensePendingOpener = null;

        this.modalContent?.removeAttribute('aria-busy');
        this.senseLoadingState?.setAttribute('hidden', '');
        this.senseErrorState?.setAttribute('hidden', '');
        this.restoreSenseStickyAtc();

        if (QuickAddModal.senseActiveModal === this) QuickAddModal.senseActiveModal = null;
      }

      suppressSenseStickyAtc() {
        if (this.senseStickyState !== null) return;

        this.senseStickyState = Array.from(document.querySelectorAll('[id^="sticky-atc-"]')).map((element) => {
          const state = {
            element,
            display: element.style.getPropertyValue('display'),
            displayPriority: element.style.getPropertyPriority('display'),
            ariaHidden: element.getAttribute('aria-hidden'),
            inert: element.getAttribute('inert'),
          };

          element.style.setProperty('display', 'none', 'important');
          element.setAttribute('aria-hidden', 'true');
          element.setAttribute('inert', '');
          return state;
        });
      }

      restoreSenseStickyAtc() {
        if (this.senseStickyState === null) return;

        this.senseStickyState.forEach(({ element, display, displayPriority, ariaHidden, inert }) => {
          if (display) {
            element.style.setProperty('display', display, displayPriority);
          } else {
            element.style.removeProperty('display');
          }

          if (ariaHidden === null) {
            element.removeAttribute('aria-hidden');
          } else {
            element.setAttribute('aria-hidden', ariaHidden);
          }

          if (inert === null) {
            element.removeAttribute('inert');
          } else {
            element.setAttribute('inert', inert);
          }
        });

        this.senseStickyState = null;
      }

      preprocessHTML(productElement) {
        if (this.isSenseModal) {
          this.preprocessSenseHTML(productElement);
          return;
        }

        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preprocessSenseHTML(productElement) {
        if (!productElement) throw new Error('Missing product element');

        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });

        this.preventDuplicatedIDs(productElement);

        if (productElement.matches('product-info')) {
          this.buildSenseProductSummary(productElement);
          this.preventVariantURLSwitching(productElement);
        } else {
          this.sanitizeSenseNode(productElement);
        }
      }

      buildSenseProductSummary(productElement) {
        const rootState = {
          id: productElement.id,
          section: productElement.dataset.section,
          originalSection: productElement.dataset.originalSection,
          productId: productElement.dataset.productId,
          url: productElement.dataset.url,
          colorClasses: Array.from(productElement.classList).filter(
            (className) => className.startsWith('color-') || className === 'gradient'
          ),
        };

        if (!rootState.section || !rootState.originalSection || !rootState.url) {
          throw new Error('Product info is missing required data attributes');
        }

        const titleSource = productElement.querySelector('.product__title h1, .product__title a, .product__title');
        const titleText = titleSource?.textContent.trim() || this.dataset.senseProductTitle?.trim();
        const priceSource = productElement.querySelector('[id^="price-"]');
        const productFormSource = productElement.querySelector('product-form');

        if (!titleText || !priceSource || !productFormSource) {
          throw new Error('Product info is missing required summary content');
        }

        const summary = document.createElement('div');
        summary.className = 'sense-complementary-modal__summary';

        const imageSources = Array.from(
          productElement.querySelectorAll('.product__media-item img, .product__media img')
        );
        const gallery = this.createSenseProductGallery(imageSources, titleText);
        if (gallery) {
          summary.appendChild(gallery);
        } else {
          summary.classList.add('sense-complementary-modal__summary--no-media');
        }

        const details = document.createElement('div');
        details.className = 'sense-complementary-modal__details';

        const title = document.createElement('h2');
        title.id = `${this.id}-ProductTitle`;
        title.className = 'sense-complementary-modal__title';
        title.textContent = titleText;
        details.appendChild(title);

        const price = priceSource.cloneNode(true);
        this.sanitizeSenseNode(price);
        details.appendChild(price);

        const variantSelectsSource = productElement.querySelector('variant-selects');
        if (variantSelectsSource) {
          const variantSelects = variantSelectsSource.cloneNode(true);
          this.sanitizeSenseNode(variantSelects);
          details.appendChild(variantSelects);
        }

        const quantitySource = productElement.querySelector('.product-form__quantity');
        if (this.isSenseQuantityRequired(quantitySource)) {
          const quantitySelector = this.createSenseQuantitySelector(quantitySource);
          if (quantitySelector) details.appendChild(quantitySelector);
        }

        details.appendChild(this.createSenseProductForm(productFormSource));
        const descriptionAccordion = this.createSenseDescriptionAccordion(productElement);
        if (descriptionAccordion) details.appendChild(descriptionAccordion);
        summary.appendChild(details);

        Array.from(productElement.attributes).forEach((attribute) => productElement.removeAttribute(attribute.name));
        if (rootState.id) productElement.id = rootState.id;
        productElement.className = ['sense-complementary-modal__product-info', ...rootState.colorClasses].join(' ');
        productElement.dataset.section = rootState.section;
        productElement.dataset.originalSection = rootState.originalSection;
        productElement.dataset.url = rootState.url;
        productElement.dataset.updateUrl = 'false';
        if (rootState.productId) productElement.dataset.productId = rootState.productId;
        productElement.replaceChildren(summary);

        if (this.senseViewProduct) this.senseViewProduct.setAttribute('href', rootState.url);
        this.removeSenseExecutableScripts(productElement);
      }

      createSenseProductGallery(imageSources, titleText) {
        const uniqueSources = imageSources.filter((image, index, images) => {
          const source = image.currentSrc || image.src || image.getAttribute('src');
          return source && images.findIndex((candidate) => (candidate.currentSrc || candidate.src || candidate.getAttribute('src')) === source) === index;
        });
        if (!uniqueSources.length) return null;

        const gallery = document.createElement('sense-modal-gallery');
        gallery.className = 'sense-complementary-modal__gallery';

        const viewport = document.createElement('div');
        viewport.className = 'sense-complementary-modal__gallery-viewport';

        const thumbnails = document.createElement('div');
        thumbnails.className = 'sense-complementary-modal__thumbnails';
        thumbnails.setAttribute('role', 'group');
        thumbnails.setAttribute('aria-label', `Изображения за ${titleText}`);

        uniqueSources.forEach((sourceImage, index) => {
          const slideId = `${this.id}-GalleryImage-${index + 1}`;
          const slide = document.createElement('div');
          slide.id = slideId;
          slide.className = 'sense-complementary-modal__gallery-slide';
          slide.dataset.senseModalGallerySlide = '';
          slide.hidden = index !== 0;
          slide.setAttribute('aria-hidden', String(index !== 0));

          const image = this.cloneSenseImage(sourceImage, 'sense-complementary-modal__image');
          image.alt = sourceImage.alt || titleText;
          slide.appendChild(image);
          viewport.appendChild(slide);

          const thumbnail = document.createElement('button');
          thumbnail.type = 'button';
          thumbnail.className = 'sense-complementary-modal__thumbnail';
          thumbnail.dataset.senseModalGalleryThumbnail = '';
          thumbnail.dataset.index = String(index);
          thumbnail.setAttribute('aria-label', `Покажи изображение ${index + 1} от ${uniqueSources.length}`);
          thumbnail.setAttribute('aria-controls', slideId);
          if (index === 0) thumbnail.setAttribute('aria-current', 'true');

          const thumbnailImage = this.cloneSenseImage(sourceImage, 'sense-complementary-modal__thumbnail-image');
          thumbnailImage.alt = '';
          thumbnailImage.removeAttribute('srcset');
          thumbnailImage.removeAttribute('sizes');
          thumbnail.appendChild(thumbnailImage);
          thumbnails.appendChild(thumbnail);
        });

        gallery.appendChild(viewport);
        if (uniqueSources.length > 1) gallery.appendChild(thumbnails);
        this.sanitizeSenseNode(gallery);
        return gallery;
      }

      cloneSenseImage(sourceImage, className) {
        const image = sourceImage.cloneNode(true);
        image.className = className;
        image.removeAttribute('id');
        image.removeAttribute('aria-controls');
        image.removeAttribute('aria-describedby');
        image.removeAttribute('aria-labelledby');
        image.removeAttribute('fetchpriority');
        image.loading = 'lazy';
        return image;
      }

      createSenseDescriptionAccordion(productElement) {
        const descriptionSource = productElement.querySelector(
          '.product__description #pdp-src, #pdp-src, [data-sense-product-description]'
        );
        if (!descriptionSource) return null;

        const source = descriptionSource.cloneNode(true);
        source
          .querySelectorAll('style, script, template, noscript, iframe, object, embed, form, input, button')
          .forEach((element) => element.remove());

        const groups = [];
        const prelude = [];
        let currentGroup = null;

        Array.from(source.childNodes).forEach((child) => {
          const isHeading = child.nodeType === Node.ELEMENT_NODE && ['H2', 'H3'].includes(child.tagName);
          if (isHeading) {
            if (currentGroup) groups.push(currentGroup);
            currentGroup = {
              title: child.textContent.replace(/\s+/g, ' ').trim(),
              nodes: [],
            };
            return;
          }

          if (currentGroup) {
            currentGroup.nodes.push(child);
          } else {
            prelude.push(child);
          }
        });

        if (currentGroup) groups.push(currentGroup);
        const hasPrelude = prelude.some((node) => node.nodeType === Node.ELEMENT_NODE || node.textContent.trim());

        if (!groups.length) {
          if (!source.textContent.trim()) return null;
          groups.push({ title: 'Описание', nodes: Array.from(source.childNodes) });
        } else if (hasPrelude) {
          groups[0].nodes.unshift(...prelude);
        }

        const container = document.createElement('section');
        container.id = `${this.id}-DescriptionAccordion`;
        container.className = 'sense-complementary-modal__description pqa-root';
        container.setAttribute(
          'aria-label',
          productElement.querySelector('#pqa-root')?.getAttribute('aria-label') || 'Навигация и детайли'
        );

        const accordion = document.createElement('div');
        accordion.id = `${this.id}-DescriptionAccordionItems`;
        accordion.className = 'pqa-acc';

        groups.forEach((group, index) => {
          if (!group.title) return;

          const details = document.createElement('details');
          const detailsId = `${this.id}-DescriptionDetails-${index + 1}`;
          const summaryId = `${detailsId}-Summary`;
          const bodyId = `${this.id}-DescriptionBody-${index + 1}`;
          details.id = detailsId;
          details.className = 'pqa-item';

          const summary = document.createElement('summary');
          summary.id = summaryId;
          summary.className = 'pqa-sum';
          summary.setAttribute('aria-controls', bodyId);
          summary.appendChild(this.createSenseDescriptionIcon(index));

          const title = document.createElement('span');
          title.className = 'pqa-title';
          title.textContent = group.title;
          summary.appendChild(title);

          const chevron = document.createElement('span');
          chevron.className = 'pqa-chevron';
          chevron.setAttribute('aria-hidden', 'true');
          chevron.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M6 9l6 6 6-6"/></svg>';
          summary.appendChild(chevron);

          const body = document.createElement('div');
          body.id = bodyId;
          body.className = 'pqa-body rte';
          body.setAttribute('role', 'region');
          body.setAttribute('aria-labelledby', summaryId);
          group.nodes.forEach((node) => {
            const safeNode = this.cloneSenseDescriptionNode(node);
            if (safeNode) body.appendChild(safeNode);
          });

          details.append(summary, body);
          accordion.appendChild(details);
        });

        if (!accordion.children.length) return null;
        container.appendChild(accordion);
        this.sanitizeSenseNode(container);
        return container;
      }

      createSenseDescriptionIcon(index) {
        const icons = [
          '<rect x="4" y="3" width="16" height="18" rx="2"></rect><path d="M8 7h8M8 11h8M8 15h6"></path>',
          '<path d="M12 3l2.3 4.7 5.2.5-3.8 3.9 1.1 5.1-4.8-2.7-4.8 2.7 1.1-5.1L4.5 8.2l5.2-.5L12 3z"></path>',
          '<path d="M12 4a2 2 0 0 1 2 2c0 .8-.5 1.3-1.2 1.8l-1.3.9"></path><path d="M3 17h18l-9-5-9 5z"></path>',
          '<path d="M12 3c3 4 5 6.5 5 9a5 5 0 0 1-10 0c0-2.5 2-5 5-9z"></path>',
          '<path d="M8 7h12M8 12h12M8 17h12"></path><circle cx="5" cy="7" r="1"></circle><circle cx="5" cy="12" r="1"></circle><circle cx="5" cy="17" r="1"></circle>',
          '<rect x="4" y="4" width="6" height="6" rx="1"></rect><rect x="14" y="4" width="6" height="6" rx="1"></rect><rect x="4" y="14" width="6" height="6" rx="1"></rect><rect x="14" y="14" width="6" height="6" rx="1"></rect>',
        ];
        const icon = document.createElement('span');
        icon.className = 'pqa-ico';
        icon.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${icons[index % icons.length]}</svg>`;
        return icon;
      }

      cloneSenseDescriptionNode(sourceNode) {
        if (sourceNode.nodeType === Node.TEXT_NODE) return document.createTextNode(sourceNode.textContent);
        if (sourceNode.nodeType !== Node.ELEMENT_NODE) return null;

        const blockedTags = new Set([
          'STYLE',
          'SCRIPT',
          'TEMPLATE',
          'NOSCRIPT',
          'IFRAME',
          'OBJECT',
          'EMBED',
          'FORM',
          'INPUT',
          'BUTTON',
          'VIDEO',
          'AUDIO',
          'SOURCE',
        ]);
        if (blockedTags.has(sourceNode.tagName)) return null;

        const allowedTags = new Set([
          'P',
          'UL',
          'OL',
          'LI',
          'A',
          'STRONG',
          'EM',
          'B',
          'I',
          'U',
          'S',
          'BR',
          'H2',
          'H3',
          'H4',
          'H5',
          'H6',
          'BLOCKQUOTE',
          'TABLE',
          'THEAD',
          'TBODY',
          'TFOOT',
          'TR',
          'TH',
          'TD',
          'CAPTION',
          'SPAN',
          'DIV',
          'SMALL',
          'SUP',
          'SUB',
          'HR',
          'IMG',
        ]);

        if (!allowedTags.has(sourceNode.tagName)) {
          const fragment = document.createDocumentFragment();
          Array.from(sourceNode.childNodes).forEach((child) => {
            const safeChild = this.cloneSenseDescriptionNode(child);
            if (safeChild) fragment.appendChild(safeChild);
          });
          return fragment;
        }

        const clone = document.createElement(sourceNode.tagName.toLowerCase());
        const globalAttributes = new Set(['class', 'title', 'lang', 'dir']);
        const tagAttributes = {
          A: new Set(['href', 'target', 'rel']),
          IMG: new Set(['src', 'srcset', 'sizes', 'alt', 'width', 'height', 'loading']),
          OL: new Set(['start', 'reversed', 'type']),
          LI: new Set(['value']),
          TH: new Set(['colspan', 'rowspan', 'scope']),
          TD: new Set(['colspan', 'rowspan']),
        };

        Array.from(sourceNode.attributes).forEach((attribute) => {
          const name = attribute.name.toLowerCase();
          if (!globalAttributes.has(name) && !tagAttributes[sourceNode.tagName]?.has(name)) return;
          if (['href', 'src'].includes(name) && !this.isSafeSenseDescriptionUrl(attribute.value)) return;
          if (name === 'srcset' && /(?:javascript|data):/i.test(attribute.value)) return;
          if (name === 'target' && !['_blank', '_self'].includes(attribute.value)) return;
          clone.setAttribute(name, attribute.value);
        });

        if (clone.tagName === 'A' && clone.getAttribute('target') === '_blank') {
          clone.setAttribute('rel', 'noopener noreferrer');
        }
        if (clone.tagName === 'IMG') clone.setAttribute('loading', 'lazy');

        Array.from(sourceNode.childNodes).forEach((child) => {
          const safeChild = this.cloneSenseDescriptionNode(child);
          if (safeChild) clone.appendChild(safeChild);
        });
        return clone;
      }

      isSafeSenseDescriptionUrl(value) {
        const trimmedValue = value.trim();
        if (!trimmedValue) return false;
        if (/^(?:#|\/|\.\/|\.\.\/|\?)/.test(trimmedValue)) return true;

        try {
          return ['http:', 'https:', 'mailto:', 'tel:'].includes(new URL(trimmedValue, window.location.origin).protocol);
        } catch (_error) {
          return false;
        }
      }

      createSenseProductForm(sourceProductForm) {
        const sourceForm = sourceProductForm.querySelector('form');
        const sourceVariantInput = sourceForm?.querySelector('input[name="id"]');
        const sourceSubmitButton = sourceForm?.querySelector(
          '.product-form__submit[type="submit"], button[type="submit"][name="add"]'
        );

        if (!sourceForm || !sourceVariantInput || !sourceSubmitButton) {
          throw new Error('Product form is missing required controls');
        }
        if (!sourceSubmitButton.querySelector('span') || !sourceSubmitButton.querySelector('.loading__spinner')) {
          throw new Error('Product submit button is missing required status elements');
        }

        const productFormTemplate = document.createElement('template');
        productFormTemplate.innerHTML = '<product-form></product-form>';
        const productForm = productFormTemplate.content.firstElementChild;
        this.copySenseElementAttributes(sourceProductForm, productForm);

        const errorMessage = sourceProductForm.querySelector('.product-form__error-message-wrapper');
        if (errorMessage) productForm.appendChild(errorMessage.cloneNode(true));

        const form = sourceForm.cloneNode(false);
        const allowedHiddenNames = new Set(['form_type', 'utf8', 'id', 'product-id']);
        sourceForm.querySelectorAll('input[type="hidden"]').forEach((input) => {
          if (allowedHiddenNames.has(input.name)) form.appendChild(input.cloneNode(true));
        });

        const sourceButtons = sourceForm.querySelector('.product-form__buttons');
        const buttons = sourceButtons ? sourceButtons.cloneNode(false) : document.createElement('div');
        buttons.classList.add('product-form__buttons');
        const submitButton = sourceSubmitButton.cloneNode(true);
        this.applySenseSubmitLabel(sourceSubmitButton, submitButton);
        buttons.appendChild(submitButton);
        form.appendChild(buttons);
        productForm.appendChild(form);

        this.sanitizeSenseNode(productForm);
        return productForm;
      }

      applySenseSubmitLabel(sourceSubmitButton, submitButton) {
        const label =
          sourceSubmitButton.disabled || sourceSubmitButton.getAttribute('aria-disabled') === 'true'
            ? this.dataset.senseLabelSoldOut
            : this.dataset.senseLabelAddToCart;
        const labelElement = submitButton.querySelector('span:not(.loading__spinner)');

        if (label && labelElement) labelElement.textContent = label;
      }

      isSenseQuantityRequired(quantitySource) {
        const quantityInput = quantitySource?.querySelector('.quantity__input');
        if (!quantityInput) return false;

        const minimum = Number(quantityInput.dataset.min || quantityInput.min || 1);
        const increment = Number(quantityInput.step || 1);
        const maximum = quantityInput.dataset.max ?? quantityInput.getAttribute('max');
        const hasPricingRules = Boolean(quantitySource.querySelector('volume-pricing, price-per-item'));

        return minimum > 1 || increment > 1 || (maximum !== null && maximum !== '') || hasPricingRules;
      }

      createSenseQuantitySelector(quantitySource) {
        const sourceQuantityInput = quantitySource?.querySelector('quantity-input');
        if (!sourceQuantityInput) return null;

        const quantity = document.createElement('div');
        this.copySenseElementAttributes(quantitySource, quantity);

        const label = quantitySource.querySelector('.quantity__label');
        if (label) quantity.appendChild(label.cloneNode(true));

        const container = document.createElement('div');
        container.className = 'price-per-item__container';
        container.appendChild(sourceQuantityInput.cloneNode(true));
        quantity.appendChild(container);

        this.sanitizeSenseNode(quantity);
        return quantity;
      }

      copySenseElementAttributes(source, destination) {
        Array.from(source.attributes).forEach((attribute) => {
          const attributeName = attribute.name.toLowerCase();
          if (attributeName.startsWith('on') || attributeName === 'data-shopify-editor-block') return;
          destination.setAttribute(attribute.name, attribute.value);
        });
      }

      sanitizeSenseNode(node) {
        node.querySelectorAll('style, iframe, object, embed').forEach((element) => element.remove());
        this.removeSenseExecutableScripts(node);

        [node, ...node.querySelectorAll('*')].forEach((element) => {
          Array.from(element.attributes || []).forEach((attribute) => {
            const attributeName = attribute.name.toLowerCase();
            if (attributeName.startsWith('on') || attributeName === 'data-shopify-editor-block') {
              element.removeAttribute(attribute.name);
            }
          });
        });

        return node;
      }

      removeSenseExecutableScripts(node) {
        node.querySelectorAll('script').forEach((script) => {
          const isSelectedVariantJSON =
            script.type.toLowerCase() === 'application/json' && script.hasAttribute('data-selected-variant');
          if (!isSelectedVariantJSON) script.remove();
        });
      }

      hasSenseExecutableScripts(node) {
        return Array.from(node.querySelectorAll('script')).some(
          (script) =>
            script.type.toLowerCase() !== 'application/json' || !script.hasAttribute('data-selected-variant')
        );
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        if (!this.isSenseModal) {
          const oldId = sectionId;
          const newId = `quickadd-${sectionId}`;
          productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
          Array.from(productElement.attributes).forEach((attribute) => {
            if (attribute.value.includes(oldId)) {
              productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
            }
          });

          productElement.dataset.originalSection = sectionId;
          return;
        }

        if (!sectionId) throw new Error('Product element is missing a section ID');

        const newId = `quickadd-${this.id}-${sectionId}`;
        [productElement, ...productElement.querySelectorAll('*')].forEach((element) => {
          Array.from(element.attributes || []).forEach((attribute) => {
            if (attribute.value.includes(sectionId)) {
              element.setAttribute(attribute.name, attribute.value.replaceAll(sectionId, newId));
            }
          });
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
